import {
  ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { AnimatePresence, motion } from "motion/react";
import { debounce, xor } from "lodash";
import Page from "../../layout/Page";
import { useBjelinTranslations } from "../../hooks/TranslationHooks";
import Map, {
  GeolocateControl,
  GeolocateControlInstance,
  Layer,
  LayerProps,
  MapProvider,
  MapRef,
  Marker,
  NavigationControl,
  ScaleControl,
  Source,
  useMap,
  ViewStateChangeEvent
} from "react-map-gl/mapbox";
import { LngLat, LngLatBounds, MapMouseEvent } from "mapbox-gl";
import {
  DelimitedArrayParam,
  DelimitedNumericArrayParam,
  StringParam,
  useQueryParam,
  withDefault
} from "use-query-params";
import classNames from "classnames";
import Icon from "../../components/Icon";
import Checkbox from "../../components/Checkbox";
import GeoSearch from "../../components/Map/GeoSearch";

import { useCountryBoundingBox } from "../../hooks/mapHooks";
import sleepUntil from "../../utils/sleepUntil";
import { useLocSettings } from "../../hooks/SettingsHooks";
import { useLang } from "../../hooks/LangHooks";
import { Spinner } from "reactstrap";
import { cmsAssetUrl } from "../../utils/CmsUtils";
import IconShowroom from "../../assets/icons/map/map-icon-showroom.svg";
import IconShop from "../../assets/icons/map/map-icon-shop.svg";
import IconAmbassador from "../../assets/icons/map/map-icon-ambassador.svg";
import IconPartner from "../../assets/icons/map/map-icon-partner.svg";
import "mapbox-gl/dist/mapbox-gl.css";
import "./style.scss";
import {
  useStoreLocation,
  useStoreLocations
} from "../../hooks/StoreLocationHooks";
import { useUser } from "../../functional-components/UserContext";
import { useTooltip } from "../../components/ToolTip";
import { useMediaQuery } from "react-responsive";
import SimpleLoadingSpinner from "../../components/SimpleLoadingSpinner";
import { formatDistance } from "../../utils/NumberUtils";
import Carousel, { CarouselHandle } from "../../components/Carousel";
import { mapBaseUrl } from "../../config/urls";
import { useNavigationContext } from "../../functional-components/NavigationContext";
import {
  StoreLocation,
  StoreLocationChain,
  StoreLocationFeature,
  StoreLocationMarker
} from "../../types/StoreLocation";
import { getCountriesForTimezone } from "countries-and-timezones";
import { getCurrentSite } from "../../utils/SiteUtils";
import { useSearchParams } from "react-router-dom";
import useTestcafe from "../../hooks/useTestcafe";
import { RemoveScroll } from "react-remove-scroll";

const typeImages: Record<string, string> = {
  showroom: IconShowroom,
  retailer: IconShop,
  ambassador: IconAmbassador,
  partner: IconPartner
};

const BREAKPOINT_WIDTH = 650;
const BREAKPOINT_HEIGHT = 600;
const FLY_TO_LOCATION_ZOOM = 11;

const AwaitMapContext = ({
  mapId = "current",
  children
}: {
  mapId: string;
  children: (map: MapRef) => ReactNode;
}) => {
  const maps = useMap();
  return maps[mapId] ? children(maps[mapId]) : null;
};

function FeatureIcon({
  feature,
  disableTooltip
}: {
  feature: Record<string, string>;
  disableTooltip?: boolean;
}) {
  const tooltipParent = useRef<HTMLDivElement>(null);
  const tooltipContent = useMemo(
    () => (
      <>
        <div className="feature-tooltip">
          <div
            id={"featureIcon" + feature.id}
            className={"feature-icon"}
            style={{ color: feature.color || "#333" }}
            dangerouslySetInnerHTML={{
              __html: feature.icon || ""
            }}
          />
          <div>
            <h4>{feature.name}</h4>
            <div>{feature.description}</div>
          </div>
        </div>
      </>
    ),
    [feature.color, feature.description, feature.icon, feature.id, feature.name]
  );
  useTooltip(tooltipParent, tooltipContent, disableTooltip);

  return (
    <div
      id={"featureIcon" + feature.id}
      ref={tooltipParent}
      className={"feature-icon"}
      style={{ color: feature.color || "#333" }}
      dangerouslySetInnerHTML={{
        __html: feature.icon || ""
      }}
    />
  );
}

function LocationDetails({
  pointId,
  allChains,
  allFeatures,
  spreadsheetId
}: {
  pointId: string;
  allChains: Record<string, StoreLocationChain>;
  allFeatures: Record<string, StoreLocationFeature>;
  spreadsheetId?: string;
}) {
  const [gid, row] = pointId.split("-").map(s => parseInt(s));
  const location = useStoreLocation(gid, row);
  const { admin } = useUser();
  const t = useBjelinTranslations();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{
        opacity: 0,
        transition: { duration: 0.3 }
      }}
      transition={{
        duration: 0.2
      }}
    >
      {location.chain && allChains[location.chain] && (
        <img
          className="chain-logo"
          src={cmsAssetUrl(allChains[location?.chain].logo?.path)}
          alt=""
          role="presentation"
        />
      )}
      <h4>{location.name}</h4>
      <div className={classNames("type-label", "type-label-" + location.type)}>
        <img
          className={"icon"}
          src={typeImages[location.type]}
          alt=""
          role="presentation"
        />
        {t("map.point-type." + location.type)}
      </div>
      <div className="contact">
        <div>{location.street}</div>
        <div>
          {location.postcode} {location.city}
          {location.state && ", " + location.state}
        </div>
        {location.tel && (
          <div>
            <a href={"tel:" + location.tel}>{location.tel}</a>
          </div>
        )}
        {location.email && (
          <div>
            <a href={"mailto:" + location.email}>{location.email}</a>
          </div>
        )}
      </div>
      <div className="features-list">
        {Object.entries(allFeatures).map(([fId, f]) => {
          return (
            location.features.includes(fId) && (
              <FeatureIcon key={fId} feature={f} />
            )
          );
        })}
      </div>
      {location.description ? (
        <div className="chain-description">{location.description}</div>
      ) : (
        location.chain &&
        allChains[location.chain]?.description && (
          <div className="chain-description">
            {allChains[location.chain].description}
          </div>
        )
      )}
      {location.website ? (
        <div className="chain-link">
          <a href={location.website} target="_blank" rel="noreferrer">
            {location.website.replace(/https?:\/\//, "")}{" "}
            <Icon name="arrow-up-right-from-square" />
          </a>
        </div>
      ) : (
        location.chain &&
        allChains[location.chain]?.url && (
          <div className="chain-link">
            <a
              href={allChains[location.chain].url}
              target="_blank"
              rel="noreferrer"
            >
              {allChains[location.chain].url?.replace(/https?:\/\//, "")}{" "}
              <Icon name="arrow-up-right-from-square" />
            </a>
          </div>
        )
      )}

      <div>
        <a
          href={`https://www.google.com/maps/@?api=1&map_action=map&center=${location.lat},${location.lng}&zoom=20`}
          target="_blank"
          rel="noreferrer"
        >
          {t("map.open.google")} <Icon name="arrow-up-right-from-square" />
        </a>
      </div>

      {admin && spreadsheetId && (
        <a
          style={{
            float: "none",
            position: "absolute",
            zIndex: 10,
            bottom: "1rem",
            lineHeight: "2rem",
            right: "1rem"
          }}
          target={"_blank"}
          rel={"noreferrer"}
          href={`https://docs.google.com/spreadsheets/d/${spreadsheetId}/edit#gid=${gid}&range=${row}:${row}`}
        >
          <Icon name="pen-to-square" type="fal" />
        </a>
      )}
    </motion.div>
  );
}

function LocationCardDetails({
  pointId,
  allFeatures,
  allChains,
  spreadsheetId
}: {
  pointId: string;
  allChains: Record<string, StoreLocationChain>;
  allFeatures: Record<string, StoreLocationFeature>;
  spreadsheetId?: string;
}) {
  const [gid, row] = pointId.split("-").map(s => parseInt(s));
  const location = useStoreLocation(gid, row);
  const isSmallScreen = useMediaQuery({ maxWidth: BREAKPOINT_WIDTH });
  const isSmallScreenHeight = useMediaQuery({ maxHeight: BREAKPOINT_HEIGHT });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{
        opacity: 0,
        transition: { duration: 0.3 }
      }}
      transition={{
        duration: 0.2
      }}
    >
      {isSmallScreen || isSmallScreenHeight ? (
        <LocationDetails
          pointId={pointId}
          allChains={allChains}
          allFeatures={allFeatures}
          spreadsheetId={spreadsheetId}
        />
      ) : (
        <>
          <div className="location-card-header">
            <img
              className={"icon"}
              src={typeImages[location.type]}
              alt=""
              role="presentation"
            />
            <h4>{location.name}</h4>
          </div>
          <div className="contact">
            {location.city}
            {location.state && ", " + location.state}
          </div>
          <div className="features-list">
            {Object.entries(allFeatures).map(([fId, f]) => {
              return (
                location.features.includes(fId) && (
                  <FeatureIcon key={fId} feature={f} disableTooltip />
                )
              );
            })}
          </div>
        </>
      )}
    </motion.div>
  );
}

function LocationInfoOverlay({
  pointId,
  pointData,
  closeCallback,
  allChains,
  allFeatures,
  spreadsheetId
}: {
  pointId: string;
  pointData: StoreLocationMarker;
  closeCallback: () => void;
  allChains: Record<string, StoreLocationChain>;
  allFeatures: Record<string, StoreLocationFeature>;
  spreadsheetId?: string;
}) {
  const location = pointData;
  return (
    <motion.div
      key={"point" + location.id}
      className="map-overlay side-panel-content"
    >
      <AnimatePresence>
        <Suspense
          fallback={
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{
                opacity: 0,
                transition: { duration: 0.3 }
              }}
              transition={{
                duration: 0.2
              }}
              aria-busy
            >
              <h4>{location.name}</h4>
              <Spinner />
            </motion.div>
          }
        >
          <LocationDetails
            pointId={pointId}
            allChains={allChains}
            allFeatures={allFeatures}
            spreadsheetId={spreadsheetId}
          />
        </Suspense>
      </AnimatePresence>
      <button
        className={"btn-close"}
        style={{
          float: "none",
          position: "absolute",
          zIndex: 10,
          top: "1rem",
          lineHeight: "2rem",
          right: "1rem"
        }}
        onClick={closeCallback}
      >
        <Icon name="xmark" type="fal" />
      </button>
    </motion.div>
  );
}

function LocationInfoCard({
  pointId,
  pointData,
  activePointId,
  allChains,
  allFeatures,
  spreadsheetId,
  setActivePointId,
  ...rest
}: {
  pointId: string;
  pointData: StoreLocation;
  activePointId?: string;
  allChains: Record<string, StoreLocationChain>;
  allFeatures: Record<string, StoreLocationFeature>;
  spreadsheetId?: string;
  setActivePointId: (pointId: string) => void;
}) {
  const { "store-map": map } = useMap();

  return (
    <motion.div
      {...rest}
      className={classNames("carousel-slide", "map-card", {
        selected: pointId === activePointId
      })}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{
        opacity: 0,
        scale: 1,
        transition: { duration: 0.3 }
      }}
      transition={{
        duration: 0.2
      }}
      onClick={e => {
        e.stopPropagation();
        map?.flyTo({
          center: [pointData.lng, pointData.lat],
          zoom: FLY_TO_LOCATION_ZOOM
        });
        setActivePointId?.(pointId);
      }}
    >
      <AnimatePresence>
        <Suspense
          fallback={
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{
                opacity: 0,
                transition: { duration: 0.3 }
              }}
              transition={{
                duration: 0.2
              }}
              aria-busy
            >
              <h4>
                <img
                  className={"icon"}
                  src={typeImages[pointData.type]}
                  alt=""
                  role="presentation"
                />
                {pointData.name}
              </h4>
              <Spinner />
            </motion.div>
          }
        >
          <LocationCardDetails
            pointId={pointId}
            allChains={allChains}
            allFeatures={allFeatures}
            spreadsheetId={spreadsheetId}
          />
        </Suspense>
      </AnimatePresence>
      <div className={"distance"}>
        {!!pointData.distance && formatDistance(pointData.distance)}
      </div>
    </motion.div>
  );
}

function InfoCardList({
  locationsByDistance,
  allChains,
  allFeatures,
  spreadsheetId,
  activePointId,
  setActivePointId,
  isMobile
}: {
  locationsByDistance: (Partial<StoreLocation> & { distance: number })[];
  allChains: Record<string, StoreLocationChain>;
  allFeatures: Record<string, StoreLocationFeature>;
  spreadsheetId?: string;
  activePointId?: string;
  setActivePointId: (pointId: string) => void;
  isMobile: boolean;
}) {
  const t = useBjelinTranslations();
  const [expanded, setExpanded] = useState(true);
  const carouselRef = useRef<CarouselHandle>(null);

  useEffect(() => {
    const activeSlideIndex = locationsByDistance.findIndex(
      p => p.id === activePointId
    );
    console.log("gotoSlide effect", activeSlideIndex);
    carouselRef.current?.gotoSlide(activeSlideIndex);
  }, [activePointId, locationsByDistance]);

  return (
    <motion.div
      key={"map-card-list"}
      className={classNames("map-card-list", {
        "map-card-list-minified": !expanded
      })}
      initial={{ opacity: 0 }}
      animate={{
        opacity: 1,
        transition: {
          delayChildren: 0.5,
          staggerChildren: 0.2
        }
      }}
      exit={{
        opacity: 0,
        transition: { duration: 0.3 }
      }}
      transition={{
        duration: 0.4
      }}
    >
      {isMobile && (
        <>
          <div className="map-card-list-header">
            {t("map.nearest-locations")}
          </div>
          <button
            className={"btn-close"}
            style={{
              float: "none",
              position: "absolute",
              zIndex: 10,
              top: "0.5rem",
              lineHeight: "2rem",
              right: "1rem"
            }}
            onClick={() => {
              setExpanded(expanded => !expanded);
            }}
          >
            {expanded ? (
              <Icon name="chevron-down" type="fal" />
            ) : (
              <Icon name="chevron-up" type="fal" />
            )}
          </button>
        </>
      )}

      <Carousel
        ref={carouselRef}
        navType="arrows"
        direction="horizontal"
        showMarkers={isMobile}
        smallMarkers
        hideButtons={isMobile}
        centeredButtons
        slidesData={locationsByDistance}
        renderSlide={(point, i) => (
          <LocationInfoCard
            key={point.id + "-" + i}
            allChains={allChains}
            allFeatures={allFeatures}
            pointId={point.id as string}
            pointData={point as StoreLocation}
            spreadsheetId={spreadsheetId}
            activePointId={activePointId}
            setActivePointId={setActivePointId}
          />
        )}
      />
    </motion.div>
  );
}

const ActivePointMarker = ({
  activePoint
}: {
  activePoint: StoreLocationMarker;
}) => {
  const { "store-map": map } = useMap();
  const [zoom, setZoom] = useState(map?.getZoom() || 1);

  const onZoom = useCallback(() => {
    setZoom(map?.getZoom() || FLY_TO_LOCATION_ZOOM);
  }, [map]);

  useEffect(() => {
    map?.on("zoomend", onZoom);
    return () => {
      map?.off("zoomend", onZoom);
    };
  }, [map, onZoom]);

  return (
    <Marker
      longitude={activePoint.lng}
      latitude={activePoint.lat}
      anchor="center"
      onClick={e => {
        e.originalEvent.stopPropagation();
        map?.flyTo({
          center: [activePoint.lng, activePoint.lat],
          zoom: zoom + 3
        });
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 64 64"
        style={{
          width:
            zoom < 8
              ? "30px"
              : zoom < 11
              ? "42px"
              : zoom < 14
              ? "56px"
              : "78px",
          height:
            zoom < 8
              ? "30px"
              : zoom < 11
              ? "42px"
              : zoom < 14
              ? "56px"
              : "78px",
          opacity: 1
        }}
      >
        <g>
          <circle
            cx="32"
            cy="32"
            r="32"
            fill="#f90"
            style={{
              transformOrigin: "center center",
              animation: "animActivePoint 3s ease-in-out infinite"
            }}
          />
          <circle cx="32" cy="32" r="18" fill="#fff" />
          <circle cx="32" cy="32" r="16" fill="#f90" />
        </g>
      </svg>
    </Marker>
  );
};

const UserLocationMarker = ({ location }: { location: LngLat }) => {
  const { "store-map": map } = useMap();
  const [zoom, setZoom] = useState(map?.getZoom() || 10);

  const onZoom = useCallback(() => {
    setZoom(map?.getZoom() || 10);
  }, [map]);

  useEffect(() => {
    map?.on("zoomend", onZoom);
    return () => {
      map?.off("zoomend", onZoom);
    };
  }, [map, onZoom]);

  return location ? (
    <Marker
      longitude={location.lng}
      latitude={location.lat}
      anchor="center"
      onClick={e => {
        e.originalEvent.stopPropagation();
        map?.flyTo({
          center: [location.lng, location.lat],
          zoom: zoom + 3
        });
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 64 64"
        style={{
          width:
            zoom < 8
              ? "30px"
              : zoom < 11
              ? "42px"
              : zoom < 14
              ? "56px"
              : "78px",
          height:
            zoom < 8
              ? "30px"
              : zoom < 11
              ? "42px"
              : zoom < 14
              ? "56px"
              : "78px",
          opacity: 0.7
        }}
      >
        <g>
          <circle
            cx="32"
            cy="32"
            r="32"
            fill="#21aaff"
            style={{
              transformOrigin: "center center",
              animation: "animActivePoint 3s ease-in-out infinite"
            }}
          />
          <circle cx="32" cy="32" r="18" fill="#fff" />
          <circle cx="32" cy="32" r="16" fill="#21aaff" />
          <circle cx="32" cy="32" r="4" fill="#fff" />
        </g>
      </svg>
    </Marker>
  ) : null;
};

const MapPageContent = () => {
  const lang = useLang();
  const isTestcafe = useTestcafe();
  const {
    features: allFeatures,
    chains: allChains,
    locations: allLocations,
    spreadsheetId
  } = useStoreLocations(lang);
  const [_searchParams, setSearchParams] = useSearchParams();

  const isSmallScreen = useMediaQuery({ maxWidth: BREAKPOINT_WIDTH });
  const isSmallScreenHeight = useMediaQuery({ maxHeight: BREAKPOINT_HEIGHT });

  const settings = useLocSettings();
  const countries =
    settings.site === "com"
      ? []
      : [settings.site, ...(settings.shipping_countries || [])];
  const t = useBjelinTranslations();
  const mapRef = useRef<MapRef>(null);
  const geolocateControlRef = useRef<GeolocateControlInstance>(null);

  const initialBounds = new LngLatBounds(
    useCountryBoundingBox(countries?.[0] || "eu")
  );
  const initialCenter = initialBounds.getCenter();
  const initialZoom = Math.max(
    4,
    8.5 -
      Math.log2(
        Math.max(
          Math.abs(initialBounds.getNorth() - initialBounds.getSouth()),
          Math.abs(initialBounds.getEast() - initialBounds.getWest())
        )
      )
  );

  type LocUrlParamType = [lat: number, lng: number, zoom: number];
  const [locParam] = useQueryParam<(number | null)[] | undefined>(
    "loc",
    useMemo(() => withDefault(DelimitedNumericArrayParam, undefined), [])
  );
  const setLocUrlParam = useCallback(
    (loc: LocUrlParamType) => {
      setSearchParams(params => {
        params.set("loc", loc.join("_"));
        return params;
      });
    },
    [setSearchParams]
  );

  const [activePointId, setActivePointId] = useQueryParam(
    "active",
    useMemo(() => withDefault(StringParam, undefined), [])
  );
  const activePoint = useMemo(
    () => allLocations.find(point => point.id === activePointId),
    [allLocations, activePointId]
  );

  const site = getCurrentSite();

  const localeTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timeZoneOverride = localStorage.getItem("timeZoneOverride");
  const timeZoneCountries = getCountriesForTimezone(
    timeZoneOverride || localeTimeZone
  );
  const timeZoneCountry: string | undefined =
    timeZoneCountries[0]?.id?.toLowerCase();

  const [userLocation, setUserlocation] = useState<LngLat | undefined>(
    locParam && locParam[0] && locParam[1]
      ? new LngLat(locParam[1], locParam[0])
      : undefined
  );
  const [showUserLocation, setShowUserLocation] = useState(false);
  const [footerExpanded, setFooterExpanded] = useState(false);

  const handleResize = useCallback(() => {
    console.log("resize");
    const map = mapRef.current?.getMap();
    map?.resize();
  }, []);

  useEffect(() => {
    const debouncedHandleResize = debounce(handleResize, 500);
    window.addEventListener("resize", debouncedHandleResize);
    return () => window.removeEventListener("resize", debouncedHandleResize);
  }, [handleResize]);

  //Trigger geolocator when it has been initialized, and no location was given in the url
  useEffect(() => {
    const _trigger = async () => {
      await sleepUntil(
        () => !!mapRef.current && !!geolocateControlRef.current,
        25000,
        1000
      );
      geolocateControlRef.current?.trigger();
    };
    if (!locParam && !isTestcafe) {
      _trigger();
    }
  }, [locParam, isTestcafe]);

  const allTypes = useMemo(() => {
    const _types = ["showroom", "retailer"];
    if (
      allLocations.findIndex(loc => loc.type === "ambassador") > -1 &&
      (timeZoneCountry === "us" || site === "us")
    )
      _types.push("ambassador");
    return _types;
  }, [allLocations, site, timeZoneCountry]);

  const [typeFilter, setTypeFilter] = useQueryParam(
    "type",
    useMemo(() => withDefault(DelimitedArrayParam, allTypes), [allTypes])
  );
  const toggleTypeFilter = useCallback(
    (f: string) => {
      setTypeFilter(filter => xor(filter, [f]));
    },
    [setTypeFilter]
  );
  /*const [featureFilter, setFeatureFilter] = useQueryParam(
    "features",
    useMemo(() => withDefault(DelimitedArrayParam, []), [])
  );
  const toggleFeatureFilter = useCallback(
    f => {
      setFeatureFilter(filter => xor(filter, [f]));
    },
    [setFeatureFilter]
  );
  const typeColors = useMemo(
    () => ({
      showroom: "#ffbc64",
      retailer: "#79d18f",
      partner: "#69b2e0"
    }),
    []
  );*/
  const [filterExpanded, setFilterExpanded] = useState(!isSmallScreen);

  const zoom = locParam?.[2] || initialZoom;

  const pointLayerDef: LayerProps = useMemo(
    () => ({
      id: "pointLayer",
      type: "symbol",
      layout: {
        "icon-image": [
          "step",
          ["zoom"],
          [
            "case",
            ["==", ["get", "type"], "showroom"],
            ["image", "map-icon-dot-showroom"],
            ["==", ["get", "type"], "retailer"],
            ["image", "map-icon-dot-shop"],
            ["==", ["get", "type"], "ambassador"],
            ["image", "map-icon-dot-partner"],
            ["==", ["get", "type"], "partner"],
            ["image", "map-icon-dot-partner"],
            ["image", "map-icon-dot-shop"]
          ],
          8,
          [
            "case",
            ["==", ["get", "type"], "showroom"],
            ["image", "map-icon-showroom"],
            ["==", ["get", "type"], "retailer"],
            ["image", "map-icon-shop"],
            ["==", ["get", "type"], "ambassador"],
            ["image", "map-icon-partner"],
            ["==", ["get", "type"], "partner"],
            ["image", "map-icon-partner"],
            ["image", "map-icon-shop"]
          ]
        ],
        "icon-size": ["step", ["zoom"], 1, 11, 1.4, 14, 2],
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
        "symbol-z-order": "source",
        "text-field": ["get", "name"],
        "text-size": 10,
        "text-variable-anchor": ["top", "bottom", "left", "right"],
        "text-radial-offset": ["step", ["zoom"], 0.5, 8, 1.6, 14, 2.9],
        "text-justify": "auto",
        "text-optional": true
      },
      paint: {
        "text-opacity": ["step", ["zoom"], 0, 8, 1],
        "text-halo-width": 1,
        "text-halo-color": "#ffffff"
      }
      /*paint: {
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 6, 5, 18, 16],
          "circle-color": [
            "case",
            ["==", ["get", "type"], "showroom"],
            colors.showroom,
            ["==", ["get", "type"], "retailer"],
            colors.retailer,
            ["==", ["get", "type"], "ambassador"],
            colors.ambassador,
            ["==", ["get", "type"], "partner"],
            colors.partner,
            colors.showroom
          ],
          "circle-stroke-color": "#fff",
          "circle-stroke-width": 2,
          "circle-pitch-scale": "map"
        }*/
    }),
    []
  );

  const filteredLocations = useMemo(() => {
    return allLocations.filter(point => typeFilter.includes(point.type));
    /*.filter(
        point =>
          intersection(featureFilter, point.features).length ===
          featureFilter.length
      );*/
  }, [allLocations, typeFilter]);

  const CARD_LIMIT = 20;

  const locationsByDistance = useMemo(() => {
    const locationsWithDistance = userLocation
      ? filteredLocations.map(point => ({
          ...point,
          distance:
            point.lng &&
            userLocation.distanceTo(new LngLat(point.lng, point.lat))
        }))
      : [];

    return locationsWithDistance
      .sort((a, b) => a.distance - b.distance)
      .slice(0, CARD_LIMIT);
  }, [filteredLocations, userLocation]);

  const activePointHasCard =
    locationsByDistance.findIndex(p => p.id === activePointId) !== -1;

  type PointsDataType = {
    type: "FeatureCollection";
    features: {
      type: "Feature";
      geometry: {
        type: "Point";
        coordinates: number[];
      };
      properties: StoreLocationMarker;
    }[];
  };

  const pointsData: PointsDataType = useMemo(() => {
    return {
      type: "FeatureCollection",
      features: filteredLocations
        .sort(
          (a, b) =>
            2000 * (a.id === activePointId ? 1 : 0) -
            2000 * (b.id === activePointId ? 1 : 0) +
            1000 * (a.type === "showroom" ? 1 : 0) -
            1000 * (b.type === "showroom" ? 1 : 0) +
            b.lat -
            a.lat
        )
        .map(point => {
          return {
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: [point.lng, point.lat]
            },
            properties: point
          };
        })
    };
  }, [activePointId, filteredLocations]);

  //console.log("pointsData", pointsData);

  // Return results using carmen geojson format:
  // https://web.archive.org/web/20210224184722/https://github.com/mapbox/carmen/blob/master/carmen-geojson.md
  // https://docs.mapbox.com/api/search/geocoding/
  type FeatureWithCarmenGeoJson = {
    type: "Feature";
    geometry: {
      type: "Point";
      coordinates: number[];
    };
    properties: StoreLocationMarker;
    place_name: string;
    center: number[];
    place_type: string[];
    relevance: number;
    bbox: [number, number, number, number];
    text: string;
    address: string;
    context: unknown[];
  };
  const forwardFeaturesGeocoder = useCallback(
    (query: string) => {
      const matchingFeatures = [];
      const q = query.toLowerCase();
      for (const _feature of pointsData.features) {
        if (
          _feature.properties.name &&
          _feature.properties.name.toLowerCase().includes(q)
        ) {
          const feature = { ...(_feature as FeatureWithCarmenGeoJson) };
          feature.place_name = feature.properties.name;
          feature.center = feature.geometry.coordinates;
          feature.place_type = ["place"];
          feature.relevance =
            feature.properties.name.toLowerCase() === q
              ? 1
              : feature.properties.name.toLowerCase().startsWith(q)
              ? 0.9
              : 0.8;
          matchingFeatures.push(feature);
        }
      }
      return matchingFeatures.sort((a, b) => {
        return b.relevance - a.relevance;
      });
    },
    [pointsData.features]
  );

  const [cursor, setCursor] = useState("auto");

  const onMouseEnter = useCallback((e: MapMouseEvent) => {
    if (e.features?.[0]) setCursor("pointer");
  }, []);
  const onMouseLeave = useCallback(() => setCursor("auto"), []);
  const onMoveEnd = useCallback(
    (e: ViewStateChangeEvent) =>
      setLocUrlParam([
        e.viewState.latitude,
        e.viewState.longitude,
        e.viewState.zoom
      ]),
    [setLocUrlParam]
  );
  const onZoomEnd = useCallback(
    (e: ViewStateChangeEvent) => {
      const zoom = e.viewState.zoom;
      if (zoom > 14) {
        mapRef.current?.easeTo({
          pitch: 45,
          duration: 1000
        });
      } else {
        mapRef.current?.easeTo({
          pitch: 0,
          duration: 1000
        });
      }
      setLocUrlParam([
        e.viewState.latitude,
        e.viewState.longitude,
        e.viewState.zoom
      ]);
    },
    [setLocUrlParam]
  );

  const onMapClick = useCallback(
    (e: MapMouseEvent) => {
      e.originalEvent.stopPropagation();
      const {
        features
        //point: { x, y }
      } = e;
      const clickedFeature = features && features[0];

      if (clickedFeature?.properties) {
        const lngLat = new LngLat(
          clickedFeature.properties.lng,
          clickedFeature.properties.lat
        );
        const zoom = mapRef.current?.getZoom();
        /*const bounds = mapRef.current.getBounds();
        const pointCount = filteredLocations.filter(point =>
          bounds.contains([point.lng, point.lat])
        ).length;*/
        const nearestDistance = filteredLocations.reduce(
          (prev, point) =>
            point.row !== clickedFeature?.properties?.row
              ? Math.min(
                  prev,
                  lngLat.distanceTo(new LngLat(point.lng, point.lat))
                )
              : prev,
          1000000 //1000 km
        );
        const minZoom =
          nearestDistance > 1
            ? Math.max(8, 21 - Math.log2(nearestDistance))
            : 21;
        //console.log("nearestDistance", nearestDistance, minZoom);
        setActivePointId(clickedFeature.properties.id);
        mapRef.current?.flyTo({
          center: lngLat,
          zoom: zoom || 0 < minZoom ? minZoom : zoom
        });
      }
    },
    [filteredLocations, setActivePointId]
  );

  const geoSearchOnResult = useCallback(
    (evt: {
      result: {
        center: [number, number];
        geometry: { type: string; coordinates: [number, number] };
      };
    }) => {
      const { result } = evt;
      const locationRes =
        result &&
        (result.center ||
          (result.geometry?.type === "Point" && result.geometry.coordinates));
      if (locationRes) {
        setUserlocation(new LngLat(locationRes[0], locationRes[1]));
        setShowUserLocation(true);
      }
    },
    []
  );

  const onGeolocate = useCallback(
    (evt: { coords: { longitude: number; latitude: number } }) => {
      //console.log("onGeolocate:", evt);
      if (evt.coords) {
        setUserlocation(new LngLat(evt.coords.longitude, evt.coords.latitude));
        setShowUserLocation(true);
      }
      return false;
    },
    []
  );

  useEffect(() => {
    console.log("userLocation changed", userLocation);
    //Make sure at least the 3 nearest locations are visible
    const thirdPoint = locationsByDistance[2];
    if (userLocation && thirdPoint) {
      const radius = userLocation.distanceTo(
        new LngLat(thirdPoint.lng, thirdPoint.lat)
      );
      if (radius < 3_000_000) {
        const bounds = userLocation.toBounds(radius * 1.2);
        mapRef.current?.fitBounds(bounds, { maxZoom: 15 });
      } else {
        //Too large bound, center on first location instead
        mapRef.current?.flyTo({
          center: locationsByDistance[0],
          zoom: 5
        });
      }
    }
  }, [locationsByDistance, userLocation]);

  const toogleFooter = useCallback(() => {
    const footer = document.querySelector(".footer");
    if (!footerExpanded) {
      footer?.scrollIntoView({ behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    setFooterExpanded(!footerExpanded);
  }, [footerExpanded]);

  return (
    <div
      className={classNames("map-layout", {
        "panel-minified":
          !filterExpanded &&
          !activePointId &&
          !(userLocation && isSmallScreenHeight)
      })}
    >
      <MapProvider>
        <RemoveScroll removeScrollBar enabled={!footerExpanded} forwardProps>
          <div className="map-wrapper">
            <Map
              id="store-map"
              antialias
              ref={mapRef}
              initialViewState={{
                latitude: locParam?.[0] || initialCenter.lat,
                longitude: locParam?.[1] || initialCenter.lng,
                zoom: zoom,
                pitch: zoom > 14 ? 45 : 0
              }}
              mapboxAccessToken={import.meta.env.VITE_MAPBOX_ACCESS_TOKEN}
              mapStyle={
                "mapbox://styles/danielpervan/clleasxf1011b01pb7so3c8nc"
              }
              style={{ position: "absolute", inset: 0 }}
              projection={"globe"}
              bearingSnap={10}
              cursor={cursor}
              interactiveLayerIds={["pointLayer"]}
              cooperativeGestures={false}
              onClick={onMapClick}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              onMoveEnd={onMoveEnd}
              onZoomEnd={onZoomEnd}
              dragRotate={false}
              language={lang}
            >
              <Source id="data-points" type="geojson" data={pointsData}>
                <Layer {...pointLayerDef} />
              </Source>
              <AnimatePresence mode={"wait"}>
                {showUserLocation && userLocation !== undefined && (
                  <UserLocationMarker
                    location={userLocation}
                    key={"user-location"}
                  />
                )}
                {activePoint !== undefined && (
                  <ActivePointMarker
                    activePoint={activePoint}
                    key={"active-location"}
                  />
                )}
              </AnimatePresence>
              <GeolocateControl
                ref={geolocateControlRef}
                showUserLocation={false}
                onGeolocate={onGeolocate}
                fitBoundsOptions={{ zoom: FLY_TO_LOCATION_ZOOM }}
                style={{
                  animation: userLocation
                    ? "none"
                    : "3s infinite animPulseStrong"
                }}
              />
              <NavigationControl showCompass={false} />
              {!isSmallScreen && <ScaleControl position="bottom-right" />}
            </Map>
          </div>
        </RemoveScroll>

        <AwaitMapContext mapId="store-map">
          {map => (
            <GeoSearch
              map={map}
              mapboxAccessToken={import.meta.env.VITE_MAPBOX_ACCESS_TOKEN}
              position="top-left"
              countries={countries?.join(",")}
              language={lang}
              localGeocoder={forwardFeaturesGeocoder}
              placeholder={t("map.search-location")}
              onResult={geoSearchOnResult}
            />
          )}
        </AwaitMapContext>

        {userLocation !== undefined && (
          <InfoCardList
            activePointId={activePointId}
            setActivePointId={setActivePointId}
            locationsByDistance={locationsByDistance}
            allFeatures={allFeatures}
            allChains={allChains}
            isMobile={isSmallScreen || isSmallScreenHeight}
            key={"card-list"}
          />
        )}
        {activePointId &&
        activePoint &&
        ((!isSmallScreen && !isSmallScreenHeight) ||
          (!activePointHasCard && (isSmallScreen || isSmallScreenHeight))) ? (
          <LocationInfoOverlay
            allChains={allChains}
            allFeatures={allFeatures}
            pointId={activePointId}
            pointData={activePoint}
            closeCallback={() => setActivePointId(undefined)}
            spreadsheetId={spreadsheetId}
          />
        ) : filterExpanded ? (
          <motion.div
            key={"filters"}
            className="map-overlay side-panel-content"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{
              opacity: 0,
              scale: 1,
              transition: { duration: 0.3 }
            }}
            transition={{
              duration: 0.2
            }}
          >
            <h3>{t("map.title")}</h3>
            <div className="filters">
              <div className="filter">
                {allTypes.map(f => (
                  <div
                    role="checkbox"
                    aria-checked={typeFilter.includes(f)}
                    tabIndex={0}
                    className={"filter-line"}
                    key={f}
                    onClick={() => toggleTypeFilter(f)}
                    onKeyDown={e => {
                      if (e.key === "Enter") toggleTypeFilter(f);
                    }}
                  >
                    <Checkbox checked={typeFilter.includes(f)} />
                    <img
                      className={"filter-icon"}
                      src={typeImages[f]}
                      alt=""
                      role="presentation"
                    />
                    <div className={"filter-text"}>{t("map.filter." + f)}</div>
                  </div>
                ))}
              </div>
              {/* Hide advanced filters until we have more data
              <div className="filter-button-wrapper">
                <Button
                  color="bjelin-grey"
                  active={advancedFilterExpanded}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    setAdvancedFilterExpanded(exp => !exp);
                  }}
                >
                  <Icon
                    name={
                      advancedFilterExpanded ? "chevron-up" : "chevron-down"
                    }
                  />{" "}
                  {t("map.advanced-filters")}
                </Button>
                {!advancedFilterExpanded && (
                  <div className="filter-summary">
                    {Object.entries(allFeatures).map(
                      ([fId, f]) =>
                        featureFilter.includes(fId) && (
                          <div
                            key={fId}
                            className={"filter-icon"}
                            style={{ color: f.color || "#333" }}
                            dangerouslySetInnerHTML={{
                              __html: f.icon
                            }}
                            title={f.name}
                          />
                        )
                    )}
                  </div>
                )}
              </div>
              {advancedFilterExpanded && (
                <div className="filter">
                  {Object.entries(allFeatures).map(([fId, f]) => (
                    <div
                      className={"filter-line"}
                      key={fId}
                      onClick={() => toggleFeatureFilter(fId)}
                    >
                      <Checkbox checked={featureFilter.includes(fId)} />
                      <div
                        className={"filter-icon"}
                        style={{ color: f.color || "#333" }}
                        dangerouslySetInnerHTML={{ __html: f.icon }}
                      />
                      <div className={"filter-text"}>{f.name}</div>
                    </div>
                  ))}
                </div>
              )}
              */}
            </div>
            <button
              className={"btn-close"}
              style={{
                float: "none",
                position: "absolute",
                zIndex: 10,
                top: "1rem",
                lineHeight: "2rem",
                right: "1rem"
              }}
              onClick={() => {
                setFilterExpanded(false);
              }}
            >
              <Icon name="xmark" type="fal" />
            </button>
          </motion.div>
        ) : (
          <motion.div
            key={"filters"}
            className="map-overlay side-panel-content filter-panel-minimized"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{
              opacity: 0,
              scale: 1,
              transition: { duration: 0.3 }
            }}
            transition={{
              duration: 0.2
            }}
          >
            <button
              style={{
                width: "3.2rem",
                fontSize: "1.5rem",
                lineHeight: "3.2rem",
                border: "none",
                outline: "none",
                background: "transparent",
                padding: "1px 6px",
                color: "inherit"
              }}
              onClick={() => {
                setFilterExpanded(true);
                const map = mapRef.current?.getMap();
                map?.resize();
              }}
            >
              <Icon name="sliders" type="fal" />
            </button>
          </motion.div>
        )}
        <button className="footer-tab" onClick={toogleFooter}>
          <div className="logo">
            <svg viewBox="0 0 139 35" style={{ width: "68px" }}>
              <g fill="#fff">
                <path d="M0 0 12.743 0C15.975.031 18.403.87 20.027 2.514 21.666 4.176 22.485 6.371 22.485 9.103 22.516 10.5 22.193 11.835 21.516 13.108 21.17 13.713 20.674 14.287 20.028 14.83 19.382 15.405 18.585 15.925 17.639 16.39L17.639 16.483C19.432 16.965 20.789 17.935 21.709 19.394 22.568 20.899 22.998 22.537 22.998 24.306 22.967 27.239 22.08 29.56 20.335 31.267 18.604 33.052 16.445 33.944 13.857 33.944L0 33.944 0 0ZM4.978 4.561 4.978 14.32 12.177 14.32C13.998 14.32 15.337 13.829 16.194 12.846 17.067 11.941 17.503 10.802 17.503 9.429 17.503 8.056 17.067 6.894 16.194 5.942 15.337 5.053 13.998 4.592 12.177 4.561L4.978 4.561ZM4.978 18.879 4.978 29.104 12.663 29.104C14.516 29.073 15.873 28.552 16.731 27.54 17.59 26.544 18.02 25.361 18.02 23.992 18.02 22.591 17.59 21.392 16.731 20.396 15.873 19.416 14.516 18.91 12.663 18.879L4.978 18.879ZM28.8526 26.8183C29.3186 27.4563 29.9206 27.9773 30.6576 28.3813 31.3946 28.8633 32.2586 29.1043 33.2516 29.1043 34.5726 29.1353 35.8326 28.6613 37.0336 27.6823 37.6176 27.2013 38.0756 26.5643 38.4066 25.7723 38.7506 25.0103 38.9236 24.0553 38.9236 22.9053L38.9236.0003 43.9016.0003 43.9016 23.3983C43.8416 26.8133 42.7606 29.4513 40.6596 31.3133 38.5726 33.2223 36.1486 34.1923 33.3876 34.2233 29.5416 34.1313 26.6486 32.6633 24.7086 29.8223L28.8526 26.8183Z" />
                <polygon points="76.155 0 81.133 0 81.133 29.104 97.159 29.104 97.159 33.944 76.155 33.944" />
                <polygon points="101.934 33.944 106.912 33.944 106.912 0 101.934 0" />
                <polygon points="113.44 0 118.133 0 133.022 24.131 133.112 24.131 133.112 0 138.09 0 138.09 33.944 133.375 33.944 118.507 9.813 118.418 9.813 118.418 33.944 113.44 33.944" />
                <polygon points="60.368 19.252 69.196 19.252 69.196 14.693 60.368 14.693" />
                <polygon points="71.417 0 50.413 0 50.413 33.944 71.417 33.944 71.417 29.104 55.391 29.104 55.391 4.841 71.417 4.841" />
              </g>
            </svg>
          </div>
          {footerExpanded ? (
            <Icon name="chevron-down" type="fal" />
          ) : (
            <Icon name="chevron-up" type="fal" />
          )}
        </button>
      </MapProvider>
    </div>
  );
};

const MapPage = () => {
  const { registerCurrentPaths } = useNavigationContext();
  const t = useBjelinTranslations();

  useEffect(() => {
    registerCurrentPaths({
      all: mapBaseUrl
    });
  }, [registerCurrentPaths]);

  return (
    <Page
      name="map-page"
      key="map-page"
      title={t("map.title.long")}
      transition="simple"
    >
      <Suspense
        fallback={
          <div className="map-layout centered" aria-busy>
            <SimpleLoadingSpinner message={t("loading")} />
          </div>
        }
      >
        <MapPageContent />
      </Suspense>
    </Page>
  );
};

export default MapPage;
